import React, { useState, useMemo } from "react";
import { Dropdown, Option, Button } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { ChevronDown20Regular, ChevronUp20Regular ,EditRegular ,Add20Filled} from "@fluentui/react-icons";
import "./CSS/FilterComponent.css";

const FilterComponent = ({ 
  availableScopes,
  queuesByScope,
  selectedScopes,
  selectedQueues,
  onScopesChange,
  onQueuesChange,
  onResetFilter
}) => {
  const { t } = useTranslation();

  // Déterminer les scopes affichés dans le dropdown
  const displayedSelectedScopes = useMemo(() => {
    if (!selectedScopes || selectedScopes.length === 0) return [];
    const allScopesExceptAll = availableScopes?.filter(s => s !== "All") || [];
    
    // Si "All" est dans selectedScopes, afficher TOUS les scopes cochés
    if (selectedScopes.includes("All")) {
      return ["All", ...allScopesExceptAll];
    }
    
    // Sinon, vérifier si tous les scopes individuels sont sélectionnés
    if (allScopesExceptAll.length > 0 && 
        selectedScopes.length === allScopesExceptAll.length &&
        allScopesExceptAll.every(s => selectedScopes.includes(s))) {
      return ["All", ...allScopesExceptAll];
    }
    
    return selectedScopes;
  }, [selectedScopes, availableScopes]);

  // Générer le texte d'affichage des scopes sélectionnés
  const selectedScopesDisplayText = useMemo(() => {
    if (!selectedScopes || selectedScopes.length === 0) return "";
    if (selectedScopes.includes("All")) {
      return t("REALTIME.ALL_SCOPES");
    }
    return selectedScopes.join(", ");
  }, [selectedScopes, t]);

  // Queues filtrées selon les scopes sélectionnés
  const filteredQueues = useMemo(() => {
    if (!queuesByScope || !selectedScopes || selectedScopes.length === 0) return [];
    // Si "All" est sélectionné, retourner toutes les queues
    if (selectedScopes.includes("All")) {
      return queuesByScope["All"] || [];
    }
    // Sinon, combiner les queues des scopes sélectionnés
    return selectedScopes.flatMap(scope => queuesByScope[scope] || []);
  }, [queuesByScope, selectedScopes]);

  // Extraire les IDs des queues sélectionnées pour l'affichage
  const selectedQueueIds = useMemo(() => {
    if (!selectedQueues || selectedQueues.length === 0) return [];
    // Vérifier si toutes les queues sont sélectionnées
    if (filteredQueues.length > 0 && selectedQueues.length === filteredQueues.length) {
      return ["All", ...selectedQueues.map(q => q.id || q.RowKey)];
    }
    return selectedQueues.map(q => q.id || q.RowKey);
  }, [selectedQueues, filteredQueues]);

  // Générer le texte d'affichage des queues sélectionnées
  const selectedQueuesDisplayText = useMemo(() => {
    if (!selectedQueues || selectedQueues.length === 0) return "";
    // Si toutes les queues sont sélectionnées, afficher "All Queues"
    if (filteredQueues.length > 0 && selectedQueues.length === filteredQueues.length) {
      return t("REALTIME.ALL_QUEUES");
    }
    return selectedQueues.map(q => q.queueName || q.name || q.RowKey).join(", ");
  }, [selectedQueues, filteredQueues, t]);

  // Options pour le dropdown Scope
  const scopeOptions = useMemo(() => {
    if (!availableScopes) return [];
    return availableScopes.map((scope) => ({
      key: scope,
      text: scope === "All" ? t("REALTIME.ALL_SCOPES") : scope,
      value: scope,
    }));
  }, [availableScopes, t]);

  // Options pour le dropdown Queue
  const queueOptions = useMemo(() => {
    const options = filteredQueues.map((queue) => ({
      key: queue.id || queue.RowKey,
      text: queue.queueName || queue.name || queue.RowKey,
      value: queue.id || queue.RowKey,
    }));
    // Ajouter l'option "All" au début
    return [
      { key: "All", text: t("REALTIME.ALL_QUEUES"), value: "All" },
      ...options
    ];
  }, [filteredQueues, t]);

  const handleScopeSelection = (e, data) => {
    console.log("Selected scopes from dropdown:", data.selectedOptions);
    const selectedOptions = [...data.selectedOptions];
    const allScopesExceptAll = availableScopes.filter(s => s !== "All");
    
    const wasAllSelected = selectedScopes.includes("All");
    const isAllSelected = selectedOptions.includes("All");
    
    let finalSelection = selectedOptions;
    
    // Si "All" vient d'être coché
    if (isAllSelected && !wasAllSelected) {
      finalSelection = ["All", ...allScopesExceptAll];
    }
    // Si "All" vient d'être décoché - TOUT décocher
    else if (!isAllSelected && wasAllSelected) {
      finalSelection = [];
    }
    // Si on décoche un scope alors que "All" était coché
    else if (wasAllSelected && isAllSelected && selectedOptions.length < selectedScopes.length) {
      finalSelection = selectedOptions.filter(s => s !== "All");
    }
    // Si on coche tous les scopes individuellement
    else if (!isAllSelected && allScopesExceptAll.length > 0 && 
             allScopesExceptAll.every(s => selectedOptions.includes(s))) {
      finalSelection = ["All", ...allScopesExceptAll];
    }
    
    if (onScopesChange) {
      onScopesChange(finalSelection);
    }
  };

  const handleQueueSelection = (e, data) => {
    const selectedOptions = [...data.selectedOptions];
    const allQueueIds = filteredQueues.map(q => q.id || q.RowKey);
    
    const wasAllSelected = selectedQueueIds.includes("All");
    const isAllSelected = selectedOptions.includes("All");
    
    let finalSelection = selectedOptions;
    
    // Si "All" vient d'être coché
    if (isAllSelected && !wasAllSelected) {
      finalSelection = ["All", ...allQueueIds];
    }
    // Si "All" vient d'être décoché
    else if (!isAllSelected && wasAllSelected) {
      finalSelection = [];
    }
    // Si on décoche une queue alors que "All" était coché
    else if (wasAllSelected && isAllSelected && selectedOptions.length < selectedQueueIds.length) {
      finalSelection = selectedOptions.filter(s => s !== "All");
    }
    // Si on coche toutes les queues individuellement
    else if (!isAllSelected && allQueueIds.length > 0 && 
             allQueueIds.every(id => selectedOptions.includes(id))) {
      finalSelection = ["All", ...allQueueIds];
    }
    
    // Filtrer pour ne garder que les IDs de vraies queues (pas "All")
    const actualQueueIds = finalSelection.filter(id => id !== "All");
    
    // Récupérer les objets complets des queues depuis filteredQueues
    const selectedQueueObjects = filteredQueues.filter(queue => 
      actualQueueIds.includes(queue.id || queue.RowKey)
    );
    
    if (onQueuesChange) {
      onQueuesChange(selectedQueueObjects);
    }
  };

  const handleReset = () => {
    if (onResetFilter) {
      onResetFilter();
    }
  };

  // Calculer le nombre de filtres actifs
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedScopes && !selectedScopes.includes("All")) count += selectedScopes.length;
    if (selectedQueues && selectedQueues.length > 0) count += selectedQueues.length;
    return count;
  }, [selectedScopes, selectedQueues]);

  return (
    <div className="filter-component">
      <div className="filter-controls">
        <div className="filter-group">
          <label>{t("REALTIME.SCOPE")}</label>
          <Dropdown
            multiselect
            placeholder={t("REALTIME.SELECT_SCOPE")}
            value={selectedScopesDisplayText}
            selectedOptions={displayedSelectedScopes}
            onOptionSelect={handleScopeSelection}
            className="filter-dropdown"
          >
            {scopeOptions.map((option) => (
              <Option key={option.key} value={option.value}>
                {option.text}
              </Option>
            ))}
          </Dropdown>
        </div>

        <div className="filter-group">
          <label>{t("REALTIME.QUEUE")}</label>
          <Dropdown
            multiselect
            placeholder={t("REALTIME.SELECT_QUEUES")}
            value={selectedQueuesDisplayText}
            selectedOptions={selectedQueueIds}
            onOptionSelect={handleQueueSelection}
            className="filter-dropdown"
          >
            {queueOptions.map((option) => (
              <Option key={option.key} value={option.value}>
                {option.text}
              </Option>
            ))}
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default FilterComponent;
